import { byId } from 'conversations/modules/endUser/selectors'
import { measureInteraction } from 'conversations/utils/MetricUtil'
import { Epic } from 'redux-observable'
import { from, interval, of } from 'rxjs'
import {
  bufferTime,
  catchError,
  debounce,
  filter,
  groupBy,
  map,
  mergeMap,
  pluck,
  switchMap,
  take,
} from 'rxjs/operators'
import {
  fetchAllConversationsAsync,
  fetchConversationAsync,
  receiveConversationUpdateFromWebsocket,
} from 'shared/store/actions/conversation'
import {
  fetchEndUserLastViewedPageAsync,
  fetchEndUsersAction,
  fetchEndUsersAsync,
  receivedPingPongEndUserPresence,
  receiveEndUserUpdate,
  recievedUserFinishedTyping,
  recievedUserStartedTyping,
  removeOldPresence,
  updatePresence,
} from 'shared/store/actions/endUser'
import { performanceTracking } from 'shared/store/actions/performanceTracking'
import { EndUser } from 'shared/typings/endUser/EndUser'
import { PresenceUpdateMessage, UserTypingMessage } from 'shared/typings/endUser/Presence'

import { isActionOf, RootAction, RootState, Services } from 'typesafe-actions'

import * as api from './api'

const fetchEndUser = (endUserIds: string[], api: any) =>
  from(api.fetchEndUser([...new Set(endUserIds)])).pipe(
    map(fetchEndUsersAsync.success as (value: unknown, index: number) => unknown),
    catchError((error: Error) => of(fetchEndUsersAsync.failure(error)))
  )

export const fetchEndUsers: Epic<RootAction, RootAction, RootState, Services> = (action$) =>
  action$.pipe(
    filter(isActionOf([fetchAllConversationsAsync.success])),
    mergeMap((action) => {
      const endUsers = action.payload.response.data
        .map((i) => i.endUser)
        .filter((endUser): endUser is EndUser => !!endUser)
      return of(fetchEndUsersAsync.success(endUsers))
    })
  )

export const fetchEndUserForNewConversation: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) =>
  action$.pipe(
    filter(isActionOf(fetchConversationAsync.success)),
    filter((action) => !byId(state.value)[action.payload.endUserId]),
    switchMap((action) => fetchEndUser([action.payload.endUserId], api))
  )

export const bufferedFetchEndUsersRequest: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) =>
  action$.pipe(
    filter(isActionOf(fetchEndUsersAction)),
    mergeMap((action) => action.payload),
    bufferTime(2000),
    filter((buffer) => buffer.length > 0),
    switchMap((ids) => fetchEndUser(ids, api))
  )

// Handles dispatching an enduser fetch when receiving an end user update event
// This epic is a HACK
// 1. At time of writing, we don't receive the correctly formatted end user object
// over the ws, so we just ignore that data and refetch from inbox-api to get the
// most up to date model with the proper data structure.
// 2. We'll receive ~10 consecutive end user update events over the ws when an email gets
// enriched, so we want to avoid hitting inbox-api that many times. To prevent this we
// buffer the events for 1s and get the distinct IDs we need to action on and only fetch those
export const receiveEndUserUpdateEpic: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) =>
  action$.pipe(
    filter(isActionOf(receiveEndUserUpdate)),
    map((action) => fetchEndUsersAction(action.payload.map((e) => e.id)))
  )

export const receiveConversationUpdateEndUserEpic: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$, state) =>
  action$.pipe(
    filter(isActionOf(receiveConversationUpdateFromWebsocket)),
    filter((action) => {
      return !byId(state.value)[action.payload.endUserId]
    }),
    mergeMap((action) => {
      const interaction = measureInteraction(
        'drift_convo_desktop_convo_updated_end_user_updates_render_time'
      )
      return [fetchEndUsersAction([action.payload.endUserId]), performanceTracking({ interaction })]
    })
  )

/*
    This runs every 5 seconds to remove old presence records and cause leaves to happen.
    be very careful making changes in response to the removeOldPresence action ac is could
    cause rerenders very often.
*/
export const setRemoveEndUserPresenceTimer: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) => {
  return action$.pipe(
    filter(isActionOf(receivedPingPongEndUserPresence)),
    take(1),
    switchMap(() => interval(5000).pipe(map(removeOldPresence)))
  )
}

export const receiveEndUserPresenceEpic: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) => {
  return action$.pipe(
    filter(isActionOf(receivedPingPongEndUserPresence)),
    pluck('payload'),
    map((payload: PresenceUpdateMessage) => {
      return updatePresence({
        id: payload.id,
        page: payload.page,
        timestamp: Date.now(),
      })
    })
  )
}

export const receiveUserStartedTyping: Epic = (action$) => {
  return action$.pipe(
    filter(isActionOf(recievedUserStartedTyping)),
    pluck('payload'),
    groupBy((payload) => `${payload.conversationId}-${payload.userId}`),
    mergeMap((group$: any) =>
      group$.pipe(
        debounce(() => interval(5000)),
        map((payload: UserTypingMessage) =>
          recievedUserFinishedTyping({
            conversationId: payload.conversationId,
            userId: payload.userId,
            userType: payload.userType,
          })
        )
      )
    )
  )
}

export const fetchEndUserLastViewedPage: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) => {
  return action$.pipe(
    filter(isActionOf(fetchEndUserLastViewedPageAsync.request)),
    switchMap((endUserId) => {
      return from(api.fetchLastViewedPage(endUserId.payload)).pipe(
        map(fetchEndUserLastViewedPageAsync.success),
        catchError((error: Error) => of(fetchEndUserLastViewedPageAsync.failure(error)))
      )
    })
  )
}
